<template>
  <div class="pdf-page">
    <div class="pdf-tools">
      <button aria-label="返回" @click="goBack"><Icon type="ios-arrow-back" /></button>
      <span>{{ pageCount ? pageCount + ' 页' : '检测报告' }}<small v-if="$route.query.serialNum">{{ maskedName }} {{ $route.query.serialNum }}</small></span>
      <button class="pdf-tools__open" @click="openOriginal">打开原报告</button>
    </div>
    <div v-if="pageCount && !errorMessage" class="pdf-navigation">
      <button :disabled="zoom <= 1" @click="zoom = Math.max(1, zoom - 0.25)">缩小</button>
      <span>{{ Math.round(zoom * 100) }}%</span>
      <button :disabled="zoom >= 2.5" @click="zoom = Math.min(2.5, zoom + 0.25)">放大</button>
      <label>页码 <input v-model="pageTarget" type="number" min="1" :max="pageCount" aria-label="跳转页码" @change="jumpToPage"></label>
      <button @click="pageTarget = 1; jumpToPage()">回顶部</button>
    </div>

    <main ref="viewer" class="pdf-viewer">
      <div v-if="loading" class="pdf-loading">
        <div class="pdf-loading__sheet"><i></i><i></i><i></i><i></i></div>
        <span>{{ downloadProgress ? '正在加载报告 ' + downloadProgress + '%' : '正在加载报告...' }}</span>
      </div>

      <div v-else-if="errorMessage" class="pdf-state pdf-state--error">
        <Icon type="ios-alert-outline" />
        <b>报告预览加载失败</b>
        <p>{{ errorMessage }}</p>
        <Button type="primary" @click="loadPdf">重新加载</Button>
        <Button @click="goBack">返回重新查询</Button>
        <Button type="primary" @click="openOriginal">打开原报告</Button>
      </div>

      <template v-else>
        <div v-if="renderedCount < pageCount" class="pdf-render-progress">正在显示第 {{ renderedCount + 1 }} / {{ pageCount }} 页</div>
        <div v-for="page in pages" :key="page" :ref="'sheet' + page" class="pdf-sheet" :style="{ width: zoom * 100 + '%' }">
          <canvas :ref="'pageCanvas' + page"></canvas>
          <span>{{ page }} / {{ pageCount }}</span>
        </div>
      </template>
    </main>
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist/es5/build/pdf'
import { retainedQuery } from '../utils/session'
// eslint-disable-next-line import/no-webpack-loader-syntax
import pdfWorkerSrc from 'file-loader?name=static/js/[name].[hash:7].[ext]!pdfjs-dist/es5/build/pdf.worker.min.js'

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerSrc

export default {
  name: 'PdfPreview',
  data () {
    return {
      loading: true,
      errorMessage: '',
      pages: [],
      pageCount: 0,
      downloadProgress: 0,
      renderedCount: 0,
      zoom: 1,
      pageTarget: 1,
      loadVersion: 0,
      loadTimer: null,
      loadingTask: null,
      renderTasks: []
    }
  },
  computed: {
    maskedName () {
      const name = String(this.$route.query.personName || '').trim()
      return name ? name.charAt(0) + (name.length > 1 ? '*'.repeat(name.length - 1) : '') : ''
    },
    reportUrl () {
      return String(this.$route.query.url || '').trim()
    }
  },
  mounted () {
    this.loadPdf()
  },
  watch: {
    reportUrl () { this.loadPdf() }
  },
  beforeDestroy () {
    this.loadVersion++
    this.cancelPdf()
  },
  methods: {
    jumpToPage () {
      const page = Math.max(1, Math.min(this.pageCount, Math.floor(Number(this.pageTarget) || 1)))
      this.pageTarget = page
      const ref = this.$refs['sheet' + page]
      const element = Array.isArray(ref) ? ref[0] : ref
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    cancelPdf () {
      window.clearTimeout(this.loadTimer)
      this.renderTasks.forEach(task => {
        if (task && typeof task.cancel === 'function') task.cancel()
      })
      this.renderTasks = []
      if (this.loadingTask && typeof this.loadingTask.destroy === 'function') {
        Promise.resolve(this.loadingTask.destroy()).catch(() => {})
      }
      this.loadingTask = null
    },
    async loadPdf () {
      const version = ++this.loadVersion
      this.cancelPdf()
      this.pages = []
      this.pageCount = 0
      this.renderedCount = 0
      this.downloadProgress = 0
      this.zoom = 1
      this.pageTarget = 1
      if (!this.reportUrl) {
        this.loading = false
        this.errorMessage = '未获取到报告地址'
        return
      }
      this.loading = true
      this.errorMessage = ''
      try {
        this.loadingTask = pdfjsLib.getDocument({
          url: this.reportUrl,
          withCredentials: false
        })
        this.loadTimer = window.setTimeout(() => {
          if (version !== this.loadVersion) return
          this.loadVersion++
          this.cancelPdf()
          this.loading = false
          this.errorMessage = '报告加载超时，请检查网络后重新加载'
        }, 60000)
        this.loadingTask.onProgress = progress => {
          if (version === this.loadVersion && progress.total > 0) {
            this.downloadProgress = Math.min(99, Math.floor(progress.loaded / progress.total * 100))
          }
        }
        const pdf = await this.loadingTask.promise
        if (version !== this.loadVersion) return
        this.pageCount = pdf.numPages
        this.pages = Array.from({ length: pdf.numPages }, (item, index) => index + 1)
        this.loading = false
        await this.$nextTick()
        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
          if (version !== this.loadVersion) return
          await this.renderPage(pdf, pageNumber, version)
          if (version !== this.loadVersion) return
          this.renderedCount = pageNumber
        }
      } catch (error) {
        if (version !== this.loadVersion) return
        if (error && error.name === 'RenderingCancelledException') return
        this.loading = false
        this.errorMessage = this.resolveError(error)
      } finally {
        if (version === this.loadVersion) window.clearTimeout(this.loadTimer)
      }
    },
    async renderPage (pdf, pageNumber, version) {
      const page = await pdf.getPage(pageNumber)
      if (version !== this.loadVersion) return
      const canvasRef = this.$refs['pageCanvas' + pageNumber]
      const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef
      if (!canvas) return

      const baseViewport = page.getViewport({ scale: 1 })
      const viewerWidth = this.$refs.viewer ? this.$refs.viewer.clientWidth : window.innerWidth
      const cssWidth = Math.max(280, Math.min(viewerWidth - 24, 900))
      const cssScale = cssWidth / baseViewport.width
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2)
      const viewport = page.getViewport({ scale: cssScale * pixelRatio })
      canvas.width = Math.floor(viewport.width)
      canvas.height = Math.floor(viewport.height)
      canvas.style.width = Math.floor(viewport.width / pixelRatio) + 'px'
      canvas.style.height = Math.floor(viewport.height / pixelRatio) + 'px'

      const renderTask = page.render({
        canvasContext: canvas.getContext('2d'),
        viewport: viewport
      })
      this.renderTasks.push(renderTask)
      await renderTask.promise
    },
    resolveError (error) {
      const message = error && error.message ? error.message : ''
      if (error && error.name === 'MissingPDFException') return '报告文件不存在或已失效，请返回重新查询'
      if (error && error.name === 'InvalidPDFException') return '报告文件暂时无法预览，请尝试打开原报告'
      if (error && (error.status === 401 || error.status === 403)) return '报告访问已失效，请返回重新查询'
      if (/cors|failed to fetch|unexpected server response/i.test(message)) {
        return '网络或报告文件访问受限，请重试或打开原报告'
      }
      return '报告暂时无法加载，请重试或打开原报告'
    },
    openOriginal () {
      if (!this.reportUrl) {
        this.$Message.warning('未获取到报告地址')
        return
      }
      window.location.href = this.reportUrl
    },
    goBack () {
      const returnTo = String(this.$route.query.returnTo || '')
      if (/^\/projects\/[^/?#]+(?:\?|$)/.test(returnTo)) {
        const index = returnTo.indexOf('?')
        this.$router.replace({ path: index < 0 ? returnTo : returnTo.substring(0, index), query: retainedQuery({ serialNum: this.$route.query.serialNum || '' }) })
      } else if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace('/home')
      }
    }
  }
}
</script>

<style scoped>
.pdf-page { min-height: 100vh; background: #e9edf3; }
.pdf-tools { position: sticky; top: 0; z-index: 10; height: 46px; padding: 0 12px; display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; background: rgba(255, 255, 255, .96); border-bottom: 1px solid #dfe4eb; }
.pdf-tools button { border: 0; background: none; color: #0f51b1; text-align: left; font-size: 22px; }
.pdf-tools > span { color: #697386; font-size: 12px; }
.pdf-tools small { display: block; max-width: 48vw; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 10px; }
.pdf-navigation { position: sticky; top: 46px; z-index: 10; display: flex; justify-content: center; align-items: center; flex-wrap: wrap; gap: 10px; padding: 8px; background: #fff; border-bottom: 1px solid #dfe4eb; }
.pdf-navigation button { border: 1px solid #d2e1ee; border-radius: 6px; padding: 6px 9px; color: #007de9; background: #f6faff; }
.pdf-navigation button:disabled { opacity: .45; }
.pdf-navigation input { width: 44px; text-align: center; border: 1px solid #cbd6e0; border-radius: 4px; }
.pdf-sheet { scroll-margin-top: 110px; }
.pdf-viewer { overflow-x: auto; }
.pdf-sheet canvas { width: 100% !important; height: auto !important; }
.pdf-render-progress { padding: 8px; color: #526983; text-align: center; font-size: 12px; }
.pdf-tools .pdf-tools__open { text-align: right; font-size: 13px; }
.pdf-viewer { max-width: 920px; min-height: calc(100vh - 46px); margin: 0 auto; padding: 12px; box-sizing: border-box; }
.pdf-state { min-height: 300px; padding: 40px 20px; border-radius: 10px; background: #fff; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; color: #6b7687; text-align: center; }
.pdf-state > i { font-size: 42px; color: #2f78d0; }
.pdf-state__loading { animation: pdf-rotate 1s linear infinite; }
.pdf-loading { min-height: 430px; display: flex; flex-direction: column; align-items: center; gap: 16px; color: #7a8493; font-size: 13px; }
.pdf-loading__sheet { width: min(82vw, 520px); height: 390px; padding: 32px 28px; box-sizing: border-box; background: #fff; }
.pdf-loading__sheet i { display: block; height: 12px; margin-bottom: 20px; border-radius: 6px; background: linear-gradient(90deg, #edf0f4 25%, #f7f8fa 50%, #edf0f4 75%); background-size: 200% 100%; animation: pdf-shimmer 1.4s infinite; }
.pdf-loading__sheet i:nth-child(1) { width: 44%; height: 20px; }
.pdf-loading__sheet i:nth-child(3) { width: 76%; }
.pdf-loading__sheet i:nth-child(4) { width: 88%; }
.pdf-state--error p { max-width: 440px; margin: 0; line-height: 1.7; }
.pdf-sheet { position: relative; margin-bottom: 12px; overflow: hidden; border-radius: 4px; background: #fff; box-shadow: 0 2px 8px rgba(19, 43, 75, .12); text-align: center; }
.pdf-sheet canvas { display: block; max-width: 100%; margin: 0 auto; background: #fff; }
.pdf-sheet span { position: absolute; right: 8px; bottom: 8px; padding: 2px 7px; border-radius: 10px; color: #fff; background: rgba(0, 0, 0, .45); font-size: 11px; }
@keyframes pdf-rotate { to { transform: rotate(360deg); } }
@keyframes pdf-shimmer { to { background-position: -200% 0; } }
</style>



// WEBPACK FOOTER //
// src/views/PdfPreview.vue